'use client';

import React, { useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Loader2, Send, ShieldCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

interface ExchangeContactDialogProps {
	isOpen: boolean;
	onClose: () => void;
	slug: string;
	ownerName: string;
	accentColor?: string;
	onSaveContact?: () => void;
}

export function ExchangeContactDialog({
	isOpen,
	onClose,
	slug,
	ownerName,
	accentColor = '#059669',
	onSaveContact,
}: ExchangeContactDialogProps) {
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [company, setCompany] = useState('');
	const [jobTitle, setJobTitle] = useState('');
	const [notes, setNotes] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) {
			toast.error('Please enter your full name');
			return;
		}
		if (!email.trim() || !email.includes('@')) {
			toast.error('Please enter a valid email address');
			return;
		}

		setIsSubmitting(true);
		try {
			const res = await fetch(`/api/experience/${encodeURIComponent(slug)}/leads`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					name: name.trim(),
					email: email.trim(),
					phone: phone.trim() || undefined,
					company: company.trim() || undefined,
					jobTitle: jobTitle.trim() || undefined,
					notes: notes.trim() || undefined,
				}),
			});

			if (!res.ok) {
				throw new Error('Failed to send contact info');
			}

			setIsSubmitted(true);
			toast.success('Your contact information was sent successfully!');
		} catch (error) {
			console.error('Contact exchange error:', error);
			toast.error('Could not send contact information. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReset = () => {
		setName('');
		setEmail('');
		setPhone('');
		setCompany('');
		setJobTitle('');
		setNotes('');
		setIsSubmitted(false);
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleReset()}>
			<DialogContent className="sm:max-w-[460px] p-6 max-h-[90vh] overflow-y-auto rounded-3xl border-slate-200 dark:border-slate-800">
				{isSubmitted ? (
					<div className="py-6 flex flex-col items-center text-center space-y-4">
						<div className="size-16 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center text-emerald-600 dark:text-emerald-400 animate-in zoom-in-75 duration-200">
							<CheckCircle2 className="size-8" />
						</div>
						<div className="space-y-1">
							<h3 className="text-xl font-bold text-slate-900 dark:text-white">Contact Sent!</h3>
							<p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
								{ownerName || 'The card owner'} has received your details.
							</p>
						</div>

						{onSaveContact && (
							<div className="pt-2 w-full">
								<Button
									type="button"
									onClick={() => {
										onSaveContact();
										handleReset();
									}}
									style={{ backgroundColor: accentColor }}
									className="w-full text-white font-semibold rounded-xl h-11"
								>
									Save {ownerName ? `${ownerName}'s` : 'Their'} Contact Card
								</Button>
							</div>
						)}

						<Button
							type="button"
							variant="outline"
							onClick={handleReset}
							className="w-full rounded-xl h-11"
						>
							Done
						</Button>
					</div>
				) : (
					<form onSubmit={handleSubmit} className="space-y-4">
						<DialogHeader className="text-left space-y-1">
							<div className="flex items-center gap-2.5 mb-1">
								<div
									className="p-2 rounded-xl text-white flex items-center justify-center"
									style={{ backgroundColor: accentColor }}
								>
									<UserPlus className="size-5" />
								</div>
								<DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
									Share Your Contact
								</DialogTitle>
							</div>
							<DialogDescription className="text-sm text-slate-500 dark:text-slate-400">
								Send your contact info directly to {ownerName || 'the card owner'} so you can stay
								in touch.
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-3 pt-2">
							<div className="space-y-1">
								<Label
									htmlFor="lead-name"
									className="text-xs font-semibold text-slate-700 dark:text-slate-300"
								>
									Full Name <span className="text-red-500">*</span>
								</Label>
								<Input
									id="lead-name"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Alex Johnson"
									required
									className="rounded-xl h-10"
								/>
							</div>

							<div className="space-y-1">
								<Label
									htmlFor="lead-email"
									className="text-xs font-semibold text-slate-700 dark:text-slate-300"
								>
									Email Address <span className="text-red-500">*</span>
								</Label>
								<Input
									id="lead-email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="alex@company.com"
									required
									className="rounded-xl h-10"
								/>
							</div>

							<div className="space-y-1">
								<Label
									htmlFor="lead-phone"
									className="text-xs font-semibold text-slate-700 dark:text-slate-300"
								>
									Phone Number (Optional)
								</Label>
								<Input
									id="lead-phone"
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									placeholder="+1 555-0199"
									className="rounded-xl h-10"
								/>
							</div>

							<div className="grid grid-cols-2 gap-2">
								<div className="space-y-1">
									<Label
										htmlFor="lead-company"
										className="text-xs font-semibold text-slate-700 dark:text-slate-300"
									>
										Company
									</Label>
									<Input
										id="lead-company"
										value={company}
										onChange={(e) => setCompany(e.target.value)}
										placeholder="Acme Inc."
										className="rounded-xl h-10"
									/>
								</div>
								<div className="space-y-1">
									<Label
										htmlFor="lead-title"
										className="text-xs font-semibold text-slate-700 dark:text-slate-300"
									>
										Job Title
									</Label>
									<Input
										id="lead-title"
										value={jobTitle}
										onChange={(e) => setJobTitle(e.target.value)}
										placeholder="VP of Growth"
										className="rounded-xl h-10"
									/>
								</div>
							</div>

							<div className="space-y-1">
								<Label
									htmlFor="lead-notes"
									className="text-xs font-semibold text-slate-700 dark:text-slate-300"
								>
									Note / Message
								</Label>
								<Textarea
									id="lead-notes"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Great meeting you at the event..."
									rows={2}
									className="rounded-xl resize-none"
								/>
							</div>
						</div>

						<div className="pt-2 space-y-2">
							<Button
								type="submit"
								disabled={isSubmitting}
								style={{ backgroundColor: accentColor }}
								className="w-full text-white font-bold rounded-xl h-11 flex items-center justify-center gap-2 cursor-pointer hover:brightness-110 transition"
							>
								{isSubmitting ? (
									<>
										<Loader2 className="size-4 animate-spin" />
										Sending...
									</>
								) : (
									<>
										<Send className="size-4" />
										Send My Contact
									</>
								)}
							</Button>

							<div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500 pt-1">
								<ShieldCheck className="size-3.5 text-emerald-600" />
								<span>Private & direct. Never shared with third parties.</span>
							</div>
						</div>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
